import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/use-toast";
import { useEffect, useRef, useState } from "react";
import {
  FormProvider,
  useForm,
  Control,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormTrigger,
  Path,
} from "react-hook-form";
import BookingAddOn from "./add-on";
import GeneralInfoForm from "./general-info-form";
import PricingAndRefund from "./pricing-and-refund";
import { Button } from "@/components/ui/button";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import { LoadingButton } from "@/components/ui/common/loading-button";
import {
  FacilitySchedule as FacilityScheduleTypes,
  ScheduleType,
} from "@/types/facility/schedule";
import {
  useCreateFacilityScheduleMutation,
  useUpdateFacilityScheduleMutation,
} from "@/services/facilityApis";
import { format, parseISO, startOfDay } from "date-fns";
import { ErrorType, FormProps } from "@/types";
import { cn } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { deriveEndDateFromCount, generateRRuleString } from "@/utils/scheduleUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

type ScheduleEditScope = "this" | "this_and_following" | "all";

interface FormStepProps {
  control: Control<FacilityScheduleTypes>;
  errors: FieldErrors<FacilityScheduleTypes>;
  watch: UseFormWatch<FacilityScheduleTypes>;
  setValue: UseFormSetValue<FacilityScheduleTypes>;
  trigger?: UseFormTrigger<FacilityScheduleTypes>;
  action: "add" | "edit";
  open: boolean;
  selectedDate?: Date;
}

interface FacilityScheduleFormProps extends FormProps<FacilityScheduleTypes> {
  selectedDate?: Date;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Step config
// ─────────────────────────────────────────────────────────────────────────────

const stepContentComponents: React.ComponentType<FormStepProps>[] = [
  GeneralInfoForm,
  BookingAddOn,
  PricingAndRefund,
];

const steps = [
  { label: "General Information", key: 1 },
  { label: "Add Ons", key: 2 },
  { label: "Refund Policy", key: 3 },
];

const getStepContent = (step: number, formProps: FormStepProps) => {
  const Component = stepContentComponents[step - 1];
  return Component ? (
    <Component {...formProps} action={formProps.action} open={formProps.open} />
  ) : null;
};

// ─────────────────────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────────────────────

const FacilityScheduleForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  data,
  setData,
  refetch,
  selectedDate,
}: FacilityScheduleFormProps) => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const [activeStep, setActiveStep] = useState(1);

  // Stores the raw API response so duration_per_slot / break_duration
  // are never overwritten in edit mode
  const originalDataRef = useRef<FacilityScheduleTypes | undefined>(undefined);

  // Stores the original rrule_str from API to detect recurrence changes
  const originalRruleRef = useRef<string>("");

  // ── Dirty flag refs for recurrence change detection ──────────────────
  // Using refs instead of string comparison to avoid false positives
  // caused by interval off-by-one in generateRRuleString
  const recurrenceDirtyRef = useRef(false);
  const recurrenceInitializedRef = useRef(false);

  // ── Scope modal state ────────────────────────────────────────────────
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [currentScope, setCurrentScope] = useState<ScheduleEditScope>("this");
  const [pendingData, setPendingData] = useState<FacilityScheduleTypes | null>(null);

  // ── Default / initial form values ───────────────────────────────────
  const initialValues: FacilityScheduleTypes = {
    org_id: orgId,
    general_info: {
      facility_id: 0,
      type: ScheduleType.FacilitySchedule,
      color: "",
      is_exclusive: false,
      max_attendees: undefined,
      break_duration: undefined,
      income_category_id: undefined,
      tax_group_id: undefined,
      advance_booking_days: undefined,
      start_date: format(new Date(), "yyyy-MM-dd"),
      always_available: false,
      member_only: false,
      price: 0,
      duration_per_slot: 0,
      start_time: "",
      end_time: "",
      location: "",
      description: "",
      name: "",
      rrule_str: "",
    },
    tier_pricing: [],
    plan_pricing: [],
    group_pricing: [],
    special_pricing: [],
    refund_policies: [],
    add_ons: [],
  };

  // ── Mutations ────────────────────────────────────────────────────────
  const [createSchedule] = useCreateFacilityScheduleMutation();
  const [updateSchedule] = useUpdateFacilityScheduleMutation();

  // ── Form ─────────────────────────────────────────────────────────────
  const form = useForm<FacilityScheduleTypes>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setError,
    setValue,
    trigger,
    formState: { isSubmitting, errors, isDirty }, // ✅ isDirty added
  } = form;

  const watcher = watch();

  // ── Watch recurrence field to detect if user changed it ─────────────
  const currentRecurrence = watch("general_info.recurrence" as any);

  // ── isRecurrenceChanged: read from dirty ref (not string comparison) ─
  // String comparison was causing false positives due to interval
  // off-by-one in generateRRuleString (effectiveInterval = interval + 1)
  const isRecurrenceChanged = recurrenceDirtyRef.current;

  // ── Populate form when editing ───────────────────────────────────────
  useEffect(() => {
    // Reset dirty flags whenever form opens/data changes
    recurrenceDirtyRef.current = false;
    recurrenceInitializedRef.current = false;

    if (action === "edit" && data) {
      originalDataRef.current = data;

      // Save original rrule_str for reference
      originalRruleRef.current = data?.general_info?.rrule_str ?? "";

      const payload = data as FacilityScheduleTypes;
      const normalized: FacilityScheduleTypes = {
        ...payload,
        refund_policies: Array.isArray(payload.refund_policies)
          ? payload.refund_policies
          : [],
        add_ons: Array.isArray(payload.add_ons) ? payload.add_ons : [],
        general_info: {
          ...payload.general_info,
          duration_per_slot:
            Number(payload.general_info?.duration_per_slot) || 0,
          break_duration: Number(payload.general_info?.break_duration) || 0,
          start_time: payload.general_info?.start_time?.substring(0, 5) || "",
          end_time: payload.general_info?.end_time?.substring(0, 5) || "",
        },
      };
      reset(normalized);

      // Mark initialized after reset settles so the watch effect
      // below doesn't fire during the reset itself
      setTimeout(() => {
        recurrenceInitializedRef.current = true;
      }, 0);
    } else if (action === "add") {
      originalDataRef.current = undefined;
      originalRruleRef.current = "";
      reset(initialValues);
    }
  }, [action, data, reset]);

  // ── Watch recurrence — only mark dirty after initialization ──────────
  // This fires only when user actually changes recurrence, not on reset
  useEffect(() => {
    if (action !== "edit") return;
    if (!recurrenceInitializedRef.current) return;
    const currentStr = JSON.stringify(currentRecurrence);
    const originalStr = JSON.stringify(originalDataRef.current?.general_info?.recurrence);

    if (currentStr !== originalStr) {
      recurrenceDirtyRef.current = true;
    }
  }, [currentRecurrence, action]);

  const toISOStringTime = (time: string | undefined | null): string => {
    if (!time) return "";
    return time + ":00";
  };

  /**
   * If the time range is exactly 1 minute short of a full slot duration,
   * round it up so the last slot isn't skipped.
   * e.g. 09:00 to 10:59 with 1h duration -> rounds to 11:00
   */
  const roundEndTimeForSlots = (startTime: string | undefined, endTime: string | undefined, durationHours: number) => {
    if (!startTime || !endTime || !durationHours) return endTime;

    const [sH, sM] = startTime.split(":").map(Number);
    const [eH, eM] = endTime.split(":").map(Number);

    const startTotal = sH * 60 + sM;
    let endTotal = eH * 60 + eM;

    // Overnight handling
    if (endTotal <= startTotal) endTotal += 1440;

    const diff = endTotal - startTotal;
    const durationMin = Math.round(durationHours * 60);

    const remaining = diff % durationMin;

    // If exactly 1 minute short of completing a full slot
    if (remaining > 0 && (durationMin - remaining) === 1) {
      const roundedEndTotal = endTotal + 1;
      const finalEndTotal = roundedEndTotal % 1440;
      const h = Math.floor(finalEndTotal / 60);
      const m = finalEndTotal % 60;
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }

    return endTime;
  };

  // ── Core submit logic ────────────────────────────────────────────────
  const processSubmit = async (
    formValues: FacilityScheduleTypes,
    scope?: ScheduleEditScope
  ) => {
    try {
      const {
        general_info,
        refund_policies,
        slot_overlaps,
        ...restData
      } = formValues;

      console.log(`this is form values in update  ${formValues}`)

      if (action === "add") {
        const start = parseISO(general_info.start_date);
        const today = startOfDay(new Date());
        if (start < today) {
          setError("general_info.start_date" as any, {
            type: "manual",
            message: "Start date cannot be in the past",
          });
          return;
        }
      }

      // Build rrule_str from recurrence config
      const recurrenceCfg = (general_info as any).recurrence as
        | import("./recurrence-selector").RecurrenceConfig
        | undefined;

      const rrule_str = recurrenceCfg?.frequency
        ? generateRRuleString(recurrenceCfg, general_info.start_date)
        : "";

      // Derive end_date
      const end_date =
        recurrenceCfg?.frequency &&
          recurrenceCfg.endCondition === "on_date" &&
          recurrenceCfg.endDate
          ? recurrenceCfg.endDate
          : recurrenceCfg?.frequency &&
            recurrenceCfg.endCondition === "after"
            ? deriveEndDateFromCount(recurrenceCfg, general_info.start_date) ||
            general_info.start_date
            : general_info.end_date || general_info.start_date;

      // In edit mode duration_per_slot and break_duration are read-only
      const originalGeneral = originalDataRef.current?.general_info;
      const finalDurationPerSlot =
        action === "edit" && originalGeneral
          ? Number(originalGeneral.duration_per_slot) ||
          general_info.duration_per_slot
          : general_info.duration_per_slot;
      const finalBreakDuration =
        action === "edit" && originalGeneral
          ? Number(originalGeneral.break_duration) ??
          general_info.break_duration
          : general_info.break_duration;

      const cleanedPayload: any = {
        org_id: orgId,
        general_info: {
          facility_id: general_info.facility_id,
          color: general_info.color,
          price_master_id: general_info.price_master_id || null,
          max_attendees: general_info.max_attendees,
          member_only: general_info.member_only,
          location: general_info.location,
          description: general_info.description,
          advance_booking_days: general_info.advance_booking_days,
          always_available: general_info.always_available,
          name: general_info.name,
          type: ScheduleType.FacilitySchedule,
          scope: scope || "this",
          trainer_name: general_info.trainer_name || "",
          // For 'all' scope, use the original schedule start_date so all past/future events are updated
          start_date: scope === "all" && originalDataRef.current?.general_info?.start_date
            ? originalDataRef.current.general_info.start_date
            : general_info.start_date,
          end_date: end_date,
          start_time: toISOStringTime(general_info.start_time),
          end_time: toISOStringTime(roundEndTimeForSlots(general_info.start_time, general_info.end_time, finalDurationPerSlot)),
          duration_per_slot: finalDurationPerSlot,
          break_duration: finalBreakDuration,
          income_category_id: general_info.income_category_id,
          tax_group_id: general_info.tax_group_id,
          rrule_str,
        },

        refund_policies:
          refund_policies?.map((r) => ({
            cancellation_period: r.cancellation_period,
            refund_percentage: r.refund_percentage,
          })) || [],

        add_ons: (restData.add_ons || []).map((addon: any) => ({
          id: addon.id,
          item_id: addon.item_id || 0,
          item_type: addon.item_type || "facility",
          max_attendees: addon.max_attendees || 0,
          price_master_id: addon.price_master_id || undefined,
        })),
      };

      if (action === "add") {
        await createSchedule(cleanedPayload as any).unwrap();
      } else {
        await updateSchedule({
          ...cleanedPayload,
          general_info: {
            ...cleanedPayload.general_info,
            id: general_info.id,
          },
        } as any).unwrap();
      }

      toast({
        variant: "success",
        title:
          action === "add"
            ? "Schedules created successfully"
            : "Schedule updated successfully",
      });

      handleClose();
      refetch();
    } catch (error) {
      console.error("Submit error:", error);
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: `${typedError.data?.detail ||
          typedError.data?.message ||
          "Something went wrong"
          }`,
      });
    }
  };

  // ── onSubmit — called by react-hook-form (last step) ────────────────
  const onSubmit = async (formValues: FacilityScheduleTypes) => {
    if (action === "edit") {
      if (!isDirty && !recurrenceDirtyRef.current) {
        await processSubmit(formValues);
        return;
      }

      console.log(`this is form values in update  ${formValues}`)

      setPendingData(formValues);
      setCurrentScope(isRecurrenceChanged ? "this_and_following" : "this");
      setShowScopeModal(true);
    } else {
      await processSubmit(formValues);
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  // ── Close / reset ────────────────────────────────────────────────────
  const handleClose = () => {
    clearErrors();
    setAction("add");
    setData(undefined);
    setActiveStep(1);
    originalDataRef.current = undefined;
    originalRruleRef.current = "";
    recurrenceDirtyRef.current = false;
    recurrenceInitializedRef.current = false;
    reset(initialValues, {
      keepIsSubmitted: false,
      keepSubmitCount: false,
      keepDirtyValues: false,
      keepDefaultValues: true,
    });
    setOpen(false);
  };

  const step1Fields: Path<FacilityScheduleTypes>[] = [
    "general_info.facility_id",
    "general_info.color",
    "general_info.income_category_id",
    "general_info.max_attendees",
    "general_info.advance_booking_days",
    "general_info.start_date",
    "general_info.start_time",
    "general_info.end_time",
    "general_info.duration_per_slot",
    "general_info.price_master_id",
    "general_info.recurrence" as any,
  ];

  const handleStep1QuickSave = async () => {
    const isValid = await form.trigger(step1Fields);
    if (!isValid) {
      toast({
        variant: "destructive",
        description: "Please fill all the mandatory fields",
      });
      return;
    }


    if (!isDirty && !recurrenceDirtyRef.current) {
      await processSubmit(form.getValues());
      return;
    }

    setPendingData(form.getValues());
    setCurrentScope(isRecurrenceChanged ? "this_and_following" : "this");
    setShowScopeModal(true);
  };

  const handleNext = async () => {
    if (activeStep >= steps.length) return;

    if (activeStep === 1) {
      const isValid = await form.trigger(step1Fields);
      if (isValid) setActiveStep((s) => s + 1);
      else {
        toast({ variant: "destructive", description: "Please fill all the mandatory fields" });
      }
    } else if (activeStep === 2) {
      const isValid = await trigger("add_ons");
      if (isValid) setActiveStep((s) => s + 1);
      else {
        toast({ variant: "destructive", description: "Please fill all required add-on fields" });
      }
    } else if (activeStep === 3) {
      const { general_info } = getValues();
      if (!general_info.facility_id) {
        toast({
          variant: "destructive",
          description: "Please select a facility first.",
        });
        return;
      }
      setActiveStep((s) => s + 1);
    } else if (activeStep === steps.length) {
      const { refund_policies: refunds } = getValues();

      if (!refunds || refunds.length === 0) {
        toast({ variant: "destructive", description: "At least one refund rule must be defined." });
        return;
      }
      if (refunds.length > 7) {
        toast({ variant: "destructive", description: "Maximum 7 refund rules are allowed." });
        return;
      }

      const refundPeriods = new Set();
      let hasInvalidRefund = false;
      let hasDuplicateRefund = false;

      for (const refund of refunds) {
        if (!refund || refund.cancellation_period === "") { hasInvalidRefund = true; break; }
        if (refundPeriods.has(refund.cancellation_period)) { hasDuplicateRefund = true; break; }
        refundPeriods.add(refund.cancellation_period);
        if (refund.refund_percentage <= 0 || refund.refund_percentage > 100) {
          toast({ variant: "destructive", description: "Refund percentage must be between 0 and 100." });
          return;
        }
      }

      if (hasInvalidRefund) {
        toast({ variant: "destructive", description: "All refund rules must have a cancellation period selected." });
        return;
      }
      if (hasDuplicateRefund) {
        toast({ variant: "destructive", description: "Duplicate cancellation periods are not allowed." });
        return;
      }

      handleSubmit(onSubmit)();
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    if (activeStep > 1) setActiveStep((s) => s - 1);
  };

  const sheetWidthClass =
    activeStep === 2
      ? "lg:!max-w-[1100px] xl:!max-w-[1320px]"
      : "lg:!max-w-[1000px]";

  console.log({ watcher });

  // ────────────────────────────────────────────────────────────────────
  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleClose}>
        <SheetContent
          hideCloseButton
          className={cn(
            "bg-formbg w-full p-0 custom-scrollbar h-full",
            sheetWidthClass
          )}
        >
          <FormProvider {...form}>
            <form
              key={action}
              noValidate
              className="pb-4"
              onSubmit={handleSubmit(onSubmit, onError)}
            >
              {/* ── Header ── */}
              <SheetHeader className="px-3 sm:px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex items-start gap-3 min-w-0">
                    <button type="button" onClick={handleClose}>
                      <i className="fa fa-xmark text-xl"></i>
                    </button>
                    <div className="min-w-0">
                      <h1 className="font-medium truncate">
                        {action === "add" ? "Create" : "Edit"} Booking Schedule
                      </h1>
                      <CompactStepIndicator
                        activeStep={activeStep}
                        steps={steps}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    {activeStep !== 1 && (
                      <Button
                        type="button"
                        className="w-9 sm:w-[80px] px-0 sm:px-3"
                        variant="outline"
                        onClick={handlePrev}
                      >
                        <i className="fa fa-arrow-left sm:hidden"></i>
                        <span className="hidden sm:inline">Previous</span>
                      </Button>
                    )}

                    {activeStep !== steps.length && (
                      <Button
                        type="button"
                        className="w-9 sm:w-[80px] px-0 sm:px-3"
                        onClick={handleNext}
                      >
                        <i className="fa fa-arrow-right sm:hidden"></i>
                        <span className="hidden sm:inline">Next</span>
                      </Button>
                    )}

                    {/* Quick Save — Step 1, edit mode only */}
                    {action === "edit" && activeStep === 1 && (
                      <LoadingButton
                        type="button"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        className="w-9 sm:w-[80px] px-0 sm:px-3"
                        onClick={handleStep1QuickSave}
                      >
                        {!isSubmitting && (
                          <i className="fa fa-floppy-disk sm:pr-2"></i>
                        )}
                        <span className="hidden sm:inline">Save</span>
                      </LoadingButton>
                    )}

                    {activeStep === steps.length && (
                      <LoadingButton
                        type="submit"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        className="w-9 sm:w-[80px] px-0 sm:px-3"
                      >
                        {!isSubmitting && (
                          <i className="fa fa-floppy-disk sm:pr-2"></i>
                        )}
                        <span className="hidden sm:inline">
                          {action === "add" ? "Save" : "Update"}
                        </span>
                      </LoadingButton>
                    )}
                  </div>
                </div>
              </SheetHeader>

              {/* ── Step content ── */}
              <SheetDescription className="h-full p-6">
                {getStepContent(activeStep, {
                  control,
                  errors,
                  watch,
                  setValue,
                  trigger,
                  action,
                  open: isOpen,
                  selectedDate,
                })}
              </SheetDescription>
            </form>
          </FormProvider>
        </SheetContent>
      </Sheet>

      {/* ── Scope Selection Modal ── */}
      <AlertDialog open={showScopeModal} onOpenChange={setShowScopeModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              This is a recurring schedule. How would you like to apply your
              changes?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <RadioGroup
            value={currentScope}
            onValueChange={(v) => setCurrentScope(v as ScheduleEditScope)}
            className="py-4"
          >
            {/* ── This only — hidden when recurrence has changed ── */}
            {!isRecurrenceChanged && (
              <div
                className={`flex items-start space-x-3 p-3 rounded-lg border mb-2 cursor-pointer transition-colors ${currentScope === "this"
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                onClick={() => setCurrentScope("this")}
              >
                <RadioGroupItem value="this" id="scope_this" className="mt-0.5 shrink-0" />
                <div>
                  <Label htmlFor="scope_this" className="cursor-pointer font-medium text-sm">
                    This event only
                  </Label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Only this single slot will be updated.
                  </p>
                </div>
              </div>
            )}

            {/* ── This and following ── */}
            <div
              className={`flex items-start space-x-3 p-3 rounded-lg border mb-2 cursor-pointer transition-colors ${currentScope === "this_and_following"
                ? "border-amber-400 bg-amber-50"
                : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              onClick={() => setCurrentScope("this_and_following")}
            >
              <RadioGroupItem
                value="this_and_following"
                id="scope_this_and_following"
                className="mt-0.5 shrink-0"
              />
              <div>
                <Label
                  htmlFor="scope_this_and_following"
                  className="cursor-pointer font-medium text-sm"
                >
                  This and following events
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  This slot and all future slots of this schedule.
                </p>
              </div>
            </div>

            {/* ── All ── */}
            <div
              className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${currentScope === "all"
                ? "border-red-400 bg-red-50"
                : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              onClick={() => setCurrentScope("all")}
            >
              <RadioGroupItem value="all" id="scope_all" className="mt-0.5 shrink-0" />
              <div>
                <Label htmlFor="scope_all" className="cursor-pointer font-medium text-sm">
                  All events
                </Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Every slot of this recurring schedule (past and future).
                </p>
              </div>
            </div>
          </RadioGroup>

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingData(null);
                setShowScopeModal(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingData) {
                  setShowScopeModal(false);
                  processSubmit(pendingData, currentScope);
                }
              }}
            >
              Update
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default FacilityScheduleForm;